import OrderApi from '@apis/order/OrderApi';
import queries from '@apis/queries';
import { datadogRum } from '@datadog/browser-rum';
import useFingerPay from '@hooks/order/useFingerPay';
import useNicePay from '@hooks/order/useNicePay';
import usePayU from '@hooks/order/usePayU';
import useLoading from '@hooks/ui/useLoading';
import useToast from '@hooks/ui/useToast';
import useProject from '@hooks/useProject';
import useProjectId from '@hooks/useProjectId';
import { ErrorResponse } from '@models/error';
import { CreateOrderRequest, CreateOrderResponse } from '@models/order';
import orderWithTotalAmount from '@providers/OrderProvider/utils/orderWithTotalAmount';
import { useFlow } from '@router/stackflow';
import useCalculatedRequest from '@store/order/hooks/useCalculatedRequest';
import { useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';

function usePrepareOrder() {
    const state = useCalculatedRequest();
    const projectId = useProjectId();
    const project = useProject();
    const [_, setCookie] = useCookies(['shopId', 'shopName', 'tempOrder']);
    const toast = useToast();
    const { push, pop } = useFlow();
    const queryClient = useQueryClient();
    const { startLoading, stopLoading } = useLoading();
    const openFingerPay = useFingerPay();
    const openPayU = usePayU();
    const openNicePay = useNicePay();
    const { t } = useTranslation('order');

    const logToDatadog = () => {
        if (import.meta.env.MODE !== 'development') {
            datadogRum.addAction('orderCheckout', {
                items: state.orderLines.map((it) => it.shopItemId),
                totalAmount: state.totalAmount,
                paymentAmount: state.paymentAmount,
                paymentMethod: state.paymentMethod,
            });
        }
    };

    const handleKicc = (checkoutUrl: string) => {
        setCookie('shopId', state.shopId, { path: '/' });
        setCookie('shopName', state.shopName, { path: '/' });
        // 주문 취소 후 복구에 필요
        setCookie('tempOrder', JSON.stringify(state), { path: '/', maxAge: 60 * 5 });
        stopLoading();
        window.location.href = checkoutUrl;
    };

    const handleFingerPay = (
        response: Required<Pick<CreateOrderResponse, 'orderNo' | 'mallId' | 'requestedAt' | 'hashString'>>,
    ) => {
        setCookie('tempOrder', JSON.stringify(state), { path: '/', maxAge: 60 * 5 });
        openFingerPay({
            ...response,
            paymentAmount: orderWithTotalAmount(state).paymentAmount,
            itemName: state.orderLines
                .map((orderLine) => orderLine.itemName)
                .join(' + '),
            returnUri: '/order/payment/finger-pay',
        });
        stopLoading();
    };

    const handleNicePay = (
        response: Required<Pick<CreateOrderResponse, 'orderNo' | 'mallId' | 'requestedAt' | 'hashString'>>,
    ) => {
        setCookie('tempOrder', JSON.stringify(state), { path: '/', maxAge: 60 * 5 });
        openNicePay({
            ...response,
            paymentAmount: orderWithTotalAmount(state).paymentAmount,
            itemName: state.orderLines
                .map((orderLine) => orderLine.itemName)
                .join(' + '),
            returnUri: '/order/payment/nice-pay',
        });
        stopLoading();
    };

    const handleEasyCard = (
        response: Required<Pick<CreateOrderResponse, 'orderNo' | 'mallId' | 'requestedAt' | 'hashString' >>
        &Pick<CreateOrderResponse, 'approvalMallId'>,
    ) => {
        const {
            orderNo, requestedAt, hashString, approvalMallId,
        } = response;
        setCookie('tempOrder', JSON.stringify(state), { path: '/', maxAge: 60 * 5 });
        stopLoading();
        openPayU({
            orderNo,
            requestedAt,
            paymentAmount: orderWithTotalAmount(state).paymentAmount,
            itemName: state.orderLines
                .map((orderLine) => orderLine.itemName)
                .join(' + '),
            approvalMallId,
            hashString,
        });
    };

    return async () => {
        startLoading();
        const request: CreateOrderRequest = { ...orderWithTotalAmount(state) };
        request.returnUrl = `${import.meta.env.VITE_BASE_URL}/purchase/complete`;
        console.log({ request });
        request.discounts = request.discounts || [];
        try {
            // 주문금액 0원일 경우 pg 미사용
            if (state.paymentAmount === 0) {
                const { orderNo } = await OrderApi.createFreeOrder({ ...request, projectId });
                logToDatadog();
                queryClient.invalidateQueries({ queryKey: queries.orders._def });
                queryClient.invalidateQueries({ queryKey: queries.user._def });
                pop(4, { animate: false });
                push('OrderLogsActivity', { duration: 'ALL' }, { animate: false });
                push('OrderLogActivity', { orderNo });
                stopLoading();
            } else {
                const res = await OrderApi.createOrder({ ...request, projectId });
                const {
                    checkoutUrl, orderNo, mallId, requestedAt, hashString, approvalMallId,
                } = res;
                logToDatadog();
                if (checkoutUrl) {
                    handleKicc(checkoutUrl);
                } else if (orderNo && mallId && requestedAt && hashString) {
                    if (state.paymentMethod === 'EASY_CARD') {
                        handleEasyCard({
                            orderNo, mallId, requestedAt, hashString, approvalMallId,
                        });
                    } else if (project?.pgVendor === 'FINGERPAY') {
                        handleFingerPay({
                            orderNo, mallId, requestedAt, hashString,
                        });
                    } else if (project?.pgVendor === 'NICEPAY') {
                        handleNicePay({
                            orderNo, mallId, requestedAt, hashString,
                        });
                    }
                }
            }
        } catch (e) {
            const axiosError = e as AxiosError<ErrorResponse>;
            console.error(e);
            stopLoading();
            toast.error(axiosError.response?.data.message || t('toast.orderError'));
        }
    };
}

export default usePrepareOrder;
